import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const imgSrc = (src) =>
  src && src.startsWith("/api/files/")
    ? `${process.env.REACT_APP_BACKEND_URL}${src}?auth=${localStorage.getItem("token")}`
    : src;

// Reusable multi-image picker (uploads to storage, keeps file paths in `images`)
export default function ImageUploader({ images, setImages, testid = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const queue = files.filter((f) => {
      if (f.size <= 10 * 1024 * 1024) return true;
      toast.error(`${f.name} is larger than 10MB and was skipped`);
      return false;
    });
    const worker = async () => {
      while (queue.length) await uploadOne(queue.shift());
    };
    await Promise.all(Array.from({ length: 4 }, worker));
  };

  const uploadOne = async (f) => {
    setUploading((n) => n + 1);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((prev) => [...prev, `/api/files/${data.id}`]);
    } catch (err) {
      toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail)}`);
    } finally {
      setUploading((n) => n - 1);
    }
  };

  const removeAt = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-3">
        {images.map((src, i) => (
          <div key={i} className="relative h-20 w-20">
            <img src={imgSrc(src)} alt="upload" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
              aria-label="Remove image"
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {uploading > 0 && (
          <div className="h-20 w-20 rounded-lg border border-slate-200 flex items-center justify-center" data-testid={`${testid}-uploading`}>
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="h-20 w-20 rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
          data-testid={`${testid}-add`}
        >
          <ImagePlus className="h-5 w-5" />
          <span className="text-[10px] mt-1">Add</span>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onPick}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

export function ImageGrid({ images }) {
  const [preview, setPreview] = useState(null);
  if (!images || images.length === 0) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 mt-3">
        {images.map((src, i) => (
          <img
            key={i}
            src={imgSrc(src)}
            loading="lazy"
            alt="attachment"
            onClick={() => setPreview(imgSrc(src))}
            className="h-24 w-24 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
            data-testid={`attachment-${i}`}
          />
        ))}
      </div>
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="image-preview-overlay"
        >
          <img src={preview} alt="preview" className="max-h-[85vh] max-w-full rounded-xl" />
        </div>
      )}
    </>
  );
}
